import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authUtil } from 'src/utils';

/**
 * Author-facing audiobook GENERATION data layer — the access-ON state of the
 * Audiobook panel (containers/book/bookAudiobook.tsx). Wraps the existing
 * credit-metered endpoints from PRs #83–87. Access is granted by either the
 * legacy full `audiobook` entitlement or the controlled voice-selection beta:
 *
 *   POST /books/:b/content/:v/audio/preview   → per-chapter cost + credit balance
 *   GET  /books/:b/content/:v/audio           → rendition status (polled)
 *   POST /books/:b/content/:v/audio           → reserve credits + start generation (202)
 *   GET  /books/:b/content/:v/audio/:part/stream-url → signed URL to preview a ready chapter
 *
 * The controlled beta requires an explicit confirmation marker on every paid
 * request. Charges real credits on convert (per chapter, 20 cr/1k words).
 */

export type AudiobookCostChapter = {
  partId: string;
  title: string;
  wordCount: number;
  credits: number;
  hasText: boolean;
  status?: string;
};

export type AudiobookCreditSummary = {
  balance?: number;
  [k: string]: any;
};

export type AudiobookCostPreview = {
  chapters: AudiobookCostChapter[];
  totalCredits: number;
  credits?: AudiobookCreditSummary;
};

export type AudiobookStatusChapter = {
  part: string;
  status: 'draft' | 'queued' | 'generating' | 'ready' | 'error';
  inStudio?: boolean;
  wordCount?: number;
  durationMs?: number;
  creditsCharged?: number;
  error?: string;
  hasAudio?: boolean;
  updatedAt?: string;
};

export type AudiobookStatus = {
  renditions: Array<{
    _id: string;
    voice?: any;
    publishedForReaders?: boolean;
    publishedAt?: string | null;
    readerReadyChapterCount?: number;
    hasUnpublishedAudio?: boolean;
    chapters: AudiobookStatusChapter[];
  }>;
};

export type AudiobookReaderAccess = {
  published: boolean;
  renditionId: string | null;
  readyChapterCount: number;
  hasUnpublishedAudio?: boolean;
  publishedAt?: string | null;
};

export type AudiobookStreamUrl = {
  url: string;
  expiresInMs?: number;
  durationMs?: number;
  voice?: any;
  timing?: any;
};

const apiBase = () =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}`;

const authedFetch = async <T>(
  path: string,
  init: RequestInit = {}
): Promise<T> => {
  const response = await fetch(`${apiBase()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'br-token': await authUtil.getFreshIdToken(),
      ...(init.headers || {}),
    },
  });
  if (!response.ok) {
    const body: any = await response.json().catch(() => ({}));
    const err: any = new Error(
      body.msg || body.error || response.statusText || 'Request failed'
    );
    err.status = response.status;
    err.code = body.error;
    err.body = body;
    throw err;
  }
  if (response.status === 204) return undefined as unknown as T;
  return response.json();
};

const IN_PROGRESS = new Set(['queued', 'generating']);

export const audiobookGenKeys = {
  preview: (b?: string, v?: string) =>
    ['audiobookGen', 'preview', b, v] as const,
  status: (b?: string, v?: string) => ['audiobookGen', 'status', b, v] as const,
};

export const useAudiobookGeneration = (
  bookId?: string,
  contentVersionId?: string,
  options: { generationEnabled?: boolean } = {}
) => {
  const queryClient = useQueryClient();
  const enabled = Boolean(bookId && contentVersionId);
  const generationEnabled = options.generationEnabled !== false;
  const base = `/books/${bookId}/content/${contentVersionId}/audio`;

  // Cost preview (whole book). POST with no partIds ⇒ all chapters.
  const previewQuery = useQuery<AudiobookCostPreview>(
    audiobookGenKeys.preview(bookId, contentVersionId),
    () =>
      authedFetch<AudiobookCostPreview>(`${base}/preview`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    {
      enabled: enabled && generationEnabled,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 30,
      retry: 1,
    }
  );

  // Rendition status, polled while any chapter is queued/generating.
  const statusQuery = useQuery<AudiobookStatus>(
    audiobookGenKeys.status(bookId, contentVersionId),
    () => authedFetch<AudiobookStatus>(base, { method: 'GET' }),
    { enabled, refetchOnWindowFocus: false, staleTime: 1000 * 3, retry: 1 }
  );

  // Flatten the latest status per part across renditions (most-recent wins).
  const statusByPart: Record<string, AudiobookStatusChapter> = {};
  (statusQuery.data?.renditions || []).forEach(r => {
    (r.chapters || []).forEach(c => {
      const prev = statusByPart[c.part];
      if (!prev || (c.updatedAt || '') >= (prev.updatedAt || '')) {
        statusByPart[c.part] = c;
      }
    });
  });
  const anyInProgress = Object.values(statusByPart).some(c =>
    IN_PROGRESS.has(c.status)
  );
  const previousInProgress = useRef(false);
  const hasObservedStatus = useRef(false);

  // A generation request reserves credits immediately, then either settles or
  // refunds them in the background. Refresh the preview (which owns the shown
  // balance) when the final queued/generating chapter reaches a terminal state.
  useEffect(() => {
    if (!statusQuery.data) return;
    if (
      hasObservedStatus.current &&
      previousInProgress.current &&
      !anyInProgress
    ) {
      queryClient.invalidateQueries(
        audiobookGenKeys.preview(bookId, contentVersionId)
      );
    }
    previousInProgress.current = anyInProgress;
    hasObservedStatus.current = true;
  }, [anyInProgress, bookId, contentVersionId, queryClient, statusQuery.data]);

  // The API returns newest-first. Keep the published rendition when one exists;
  // otherwise offer the newest rendition that has ready audio as the publish
  // candidate. Generation remains private until this explicit mutation runs.
  const renditions = statusQuery.data?.renditions || [];
  const publishedRendition = renditions.find(r => r.publishedForReaders);
  const readyRendition = renditions.find(r =>
    (r.chapters || []).some(c => c.status === 'ready' && c.hasAudio !== false)
  );
  const accessRendition = publishedRendition || readyRendition;
  const readerAccess: AudiobookReaderAccess = {
    published: Boolean(publishedRendition),
    renditionId: accessRendition?._id || null,
    readyChapterCount: publishedRendition?.readerReadyChapterCount ?? (
      accessRendition
        ? accessRendition.chapters.filter(
            c => c.status === 'ready' && c.hasAudio !== false
          ).length
        : 0
    ),
    hasUnpublishedAudio: publishedRendition?.hasUnpublishedAudio,
    publishedAt: publishedRendition?.publishedAt,
  };

  // Poll status every 4s while a chapter is generating.
  const refetchStatus = statusQuery.refetch;
  useEffect(() => {
    if (!enabled || !anyInProgress) return undefined;
    const id = setInterval(() => {
      refetchStatus();
    }, 4000);
    return () => clearInterval(id);
  }, [enabled, anyInProgress, refetchStatus]);

  // Start generation for a chapter selection (empty ⇒ all).
  const convert = useMutation(
    (partIds: string[]) =>
      authedFetch<any>(base, {
        method: 'POST',
        body: JSON.stringify({ partIds, confirmCredits: true }),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          audiobookGenKeys.status(bookId, contentVersionId)
        );
        queryClient.invalidateQueries(
          audiobookGenKeys.preview(bookId, contentVersionId)
        );
        queryClient.invalidateQueries(['audiobookVoiceSamples', bookId]);
        // Kick an immediate status refetch so the row flips to "queued".
        statusQuery.refetch();
      },
    }
  );

  const getStreamUrl = (partId: string) =>
    authedFetch<AudiobookStreamUrl>(`${base}/${partId}/stream-url`, {
      method: 'GET',
    });

  const setReaderAccess = useMutation(
    (published: boolean) => {
      if (published && !readerAccess.renditionId) {
        return Promise.reject(
          new Error('No ready audiobook can be published.')
        );
      }
      return authedFetch<{
        published: boolean;
        renditionId: string | null;
        readyChapterCount: number;
      }>(`${base}/reader-access`, {
        method: 'PUT',
        body: JSON.stringify({
          published,
          ...(published ? { renditionId: readerAccess.renditionId } : {}),
        }),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          audiobookGenKeys.status(bookId, contentVersionId)
        );
        statusQuery.refetch();
      },
    }
  );

  return {
    preview: previewQuery.data,
    // isInitialLoading (not isLoading): a DISABLED react-query v4 query reports
    // isLoading=true forever, which would wedge the panel on "Estimating cost…".
    previewLoading: previewQuery.isInitialLoading,
    previewError: previewQuery.error as any,
    statusLoading: statusQuery.isInitialLoading,
    statusByPart,
    anyInProgress,
    convert,
    getStreamUrl,
    readerAccess,
    setReaderAccess,
    refetchStatus,
  };
};

export default useAudiobookGeneration;
